import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ChangePassword({ forced = false, onDone }) {
  const { changePassword, logout, signOutEverywhere } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);

  const handleSignOutEverywhere = async () => {
    setSigningOutAll(true);
    try {
      await signOutEverywhere();
      toast.success("Signed out of all devices");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not sign out everywhere");
      setSigningOutAll(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters.");
    if (next !== confirm) return setError("New passwords don't match.");
    setLoading(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
      if (onDone) onDone();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const form = (
    <form onSubmit={submit} data-testid="change-password-form" className="rounded-3xl bg-white p-8 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)]">
      <div className="flex items-center gap-2 mb-1 text-charcoal-800">
        <ShieldCheck size={20} className="text-terracotta" strokeWidth={1.5} />
        <h1 className="font-serif text-2xl">{forced ? "Set a new password" : "Change password"}</h1>
      </div>
      <p className="text-sm text-charcoal-500 mb-6">
        {forced ? "For security, please choose your own password before continuing." : "Update the password for your admin account."}
      </p>
      <label className="text-sm font-medium text-charcoal-600 mb-2 block">Current password</label>
      <input data-testid="cp-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
        className="w-full bg-white border border-sand-200 rounded-lg px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-sage/50" />
      <label className="text-sm font-medium text-charcoal-600 mb-2 block">New password</label>
      <input data-testid="cp-new" type="password" required value={next} onChange={(e) => setNext(e.target.value)}
        className="w-full bg-white border border-sand-200 rounded-lg px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-sage/50" />
      <label className="text-sm font-medium text-charcoal-600 mb-2 block">Confirm new password</label>
      <input data-testid="cp-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
        className="w-full bg-white border border-sand-200 rounded-lg px-4 py-3 mb-6 focus:outline-none focus:ring-2 focus:ring-sage/50" />
      {error && <p data-testid="cp-error" className="text-sm text-destructive mb-4">{error}</p>}
      <button data-testid="cp-submit" disabled={loading}
        className="w-full rounded-full bg-terracotta py-3 text-white font-medium hover:bg-terracotta-hover transition-colors disabled:opacity-60">
        {loading ? "Saving…" : "Update password"}
      </button>
      {forced && (
        <button type="button" data-testid="cp-logout" onClick={logout} className="w-full mt-3 text-sm text-charcoal-500 hover:text-terracotta">
          Sign out instead
        </button>
      )}
    </form>
  );

  if (!forced) return (
    <div data-testid="admin-change-password" className="max-w-md space-y-4">
      {form}
      <div className="rounded-3xl bg-white p-6 border border-sand-200">
        <h2 className="font-serif text-lg text-charcoal-800 mb-1">Sign out everywhere</h2>
        <p className="text-sm text-charcoal-500 mb-4">
          End every active session for your account on all devices. You'll need to sign in again here.
        </p>
        <button type="button" data-testid="sign-out-everywhere-btn" disabled={signingOutAll}
          onClick={handleSignOutEverywhere}
          className="rounded-full border border-terracotta text-terracotta px-5 py-2.5 text-sm font-medium hover:bg-terracotta hover:text-white transition-colors disabled:opacity-60">
          {signingOutAll ? "Signing out…" : "Sign out of all devices"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#EAF0EE] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src="/brand/logo-mark.png" alt="Hola Movers" className="h-12 w-auto" />
          <span className="font-serif text-2xl text-charcoal-800">Hola Movers</span>
        </div>
        {form}
      </div>
    </div>
  );
}
